import React, { useState, useEffect, useCallback } from "react";
import { apiRequests } from "../../shared/api/api";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { Button } from "src/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Label } from "src/components/ui/label";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { Badge } from "src/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle} from "src/components/ui/card";
import { Skeleton } from "src/components/ui/skeleton";
import { Separator } from "src/components/ui/separator";
import { toast } from "sonner";

import {
  Plus,
  QrCode,
  BarChart3,
  Trash2,
  Pencil,
  Eye,
  RotateCcw,
  Users,
  CheckCircle2,
  XCircle,
  Copy,
} from "lucide-react";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const C_BTN = {
  Max: "#1677ff",
  Telegram: "#0088cc",
  SMS: "#52c41a",
  Other: "#fa8c16",
};
const C_CHART = ["#1677ff", "#52c41a", "#faad14", "#f5222d", "#722ed1", "#13c2c2"];
const QR_DOMAIN = process.env.REACT_APP_QR_DOMAIN || "https://qrrun.ru";
const QR_SRC = (code, size = 100) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    `${QR_DOMAIN}/qr/${code}`
  )}`;
const SHORT_URL = (code) => `${QR_DOMAIN.replace(/^https?:\/\//, "")}/${code}`;

const ButtonQRBlock = ({ buttons = [] }) => {
  if (!buttons.length) return null;
  return (
    <div className="flex flex-wrap gap-3 justify-center">
      {buttons.map((btn) => (
        <div key={btn.type} className="flex flex-col items-center gap-1.5 p-3 border rounded-lg bg-muted/30 w-32">
          <img src={QR_SRC(btn.short_code, 80)} alt={btn.type} className="w-20 h-20" />
          <Badge
            variant="outline"
            className="text-xs px-2"
            style={{ borderColor: C_BTN[btn.type], color: C_BTN[btn.type] }}
          >
            {btn.type}
          </Badge>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground truncate max-w-[80px]">
              {SHORT_URL(btn.short_code)}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${QR_DOMAIN}/qr/${btn.short_code}`);
                toast.success("Ссылка скопирована");
              }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const PreviewModal = ({ open, onOpenChange, page }) => {
  if (!page) return null;
  const isSVG = (s) => typeof s === "string" && s.trim().startsWith("<svg");
  const buttons = page.buttons || [];
  const landing = `${QR_DOMAIN}/qr/${page.short_code}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Превью мини-лендинга</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 text-center">
          {page.logo &&
            (isSVG(page.logo) ? (
              <div dangerouslySetInnerHTML={{ __html: page.logo }} className="h-10" />
            ) : (
              <div className="text-4xl">{page.logo}</div>
            ))}
          {page.image_url &&
            (isSVG(page.image_url) ? (
              <div dangerouslySetInnerHTML={{ __html: page.image_url }} className="max-h-28 overflow-hidden" />
            ) : (
              <img
                src={page.image_url}
                alt="cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
                className="w-full max-h-28 object-cover rounded-lg"
              />
            ))}

          <div className="space-y-1">
            <h3 className="text-lg">{page.name}</h3>
            {page.description && (
              <p className="text-sm text-muted-foreground">{page.description}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 justify-center w-full">
            {buttons.map((btn) => (
              <Button
                key={btn.type}
                style={{ backgroundColor: C_BTN[btn.type] || "#1677ff" }}
                className="text-white text-sm"
                size="sm"
              >
                {btn.label}
              </Button>
            ))}
          </div>

          <Separator className="w-full" />
          <ButtonQRBlock buttons={buttons} />

          <Separator className="w-full" />
          <div className="space-y-2 w-full">
            <img src={QR_SRC(page.short_code, 120)} alt="QR" className="w-28 h-28 mx-auto" />
            <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
              <span className="truncate max-w-[200px]">{landing}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(landing);
                  toast.success("Ссылка скопирована");
                }}
                className="hover:text-foreground transition-colors"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              UTM: {page.utm_source} / {page.utm_medium} / {page.utm_campaign}
            </p>
            {page.auto_tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-center">
                {page.auto_tags.map((t) => (
                  <Badge key={t} variant="secondary" className="text-xs">
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VisitsModal = ({ open, onOpenChange, page, visits, loading }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{page?.name} — история переходов</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : visits.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            Нет переходов по QR-коду
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Дата/время</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Кнопка</TableHead>
                <TableHead>Цель</TableHead>
                <TableHead>Yandex CID</TableHead>
                <TableHead>Google CID</TableHead>
                <TableHead>Реферер</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visits.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="text-sm">
                    {v.created_at ? new Date(v.created_at).toLocaleString("ru-RU") : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{v.ip || "—"}</TableCell>
                  <TableCell>
                    {v.button_type ? (
                      <Badge variant="outline" style={{ borderColor: C_BTN[v.button_type], color: C_BTN[v.button_type] }}>
                        {v.button_type}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {v.target_reached ? (
                      <Badge className="bg-green-50 text-green-700 border-green-200 border gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Регистрация
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Нет
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{v.yandex_cid || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{v.google_cid || "—"}</TableCell>
                  <TableCell className="max-w-[150px] truncate text-xs text-muted-foreground">
                    {v.referer || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DetailCharts = ({ stats }) => {
  if (!stats)
    return (
      <div className="flex justify-center py-8">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  const { daily = [], buttons = [] } = stats;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Динамика переходов и регистраций</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daily} margin={{ left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="visits" fill="#1677ff" name="Переходы" radius={[3, 3, 0, 0]} />
              <Bar dataKey="registrations" fill="#52c41a" name="Регистрации" radius={[3, 3, 0, 0]} />
              <Bar dataKey="clicks" fill="#fa8c16" name="Клики" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Конверсия CR%</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={daily} margin={{ left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <RechartsTooltip formatter={(v) => `${v}%`} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
              <Line type="monotone" dataKey="conversion" stroke="#722ed1" strokeWidth={2} dot={{ r: 3 }} name="CR%" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {buttons.length > 0 && (
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Распределение по кнопкам</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={buttons}
                  dataKey="count"
                  nameKey="button_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ button_type, count }) => `${button_type}: ${count}`}
                >
                  {buttons.map((b, i) => (
                    <Cell key={b.button_type} fill={C_BTN[b.button_type] || C_CHART[i % 6]} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const CampaignFormModal = ({ open, onOpenChange, onSave, initial }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [collectUtm, setCollectUtm] = useState(true);

  useEffect(() => {
    if (open) {
      const v = initial || {};
      setFormData({
        source_name: v.source_name || "",
        name: v.name || "",
        description: v.description || "",
        image_url: v.image_url || "",
        logo: v.logo || "",
        utm_source: v.utm_source || "qr_cashier",
        utm_medium: v.utm_medium || "qr_physical",
        utm_campaign: v.utm_campaign || "loyalty_card",
        collect_form_utm: v.collect_form_utm !== false,
        auto_tags: (v.auto_tags || []).join(", "),
        status: v.status || "active",
      });
      setCollectUtm(v.collect_form_utm !== false);
    }
  }, [open, initial]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        auto_tags: formData.auto_tags
          ? formData.auto_tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        buttons: [
          { type: "Max", label: "Через Max" },
          { type: "Telegram", label: "Telegram" },
          { type: "SMS", label: "Через SMS" },
        ],
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            {initial ? "Редактировать кампанию" : "Новая кампания"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 z-2000">
          <div className="space-y-1.5">
            <Label htmlFor="source_name" className="text-sm">Назовите данный источник</Label>
            <Input
              id="source_name"
              value={formData.source_name}
              onChange={(e) => handleChange("source_name", e.target.value)}
              placeholder="Касса №1 — основная"
            />
            <p className="text-xs text-muted-foreground">
              Например: «Касса №3», «Витрина входная», «Листовка у стойки»
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-sm after:content-['*'] after:ml-0.5 after:text-red-500">
              Название кампании
            </Label>
            <Input
              id="name"
              required
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Premium Кешбэк 2025"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-sm">Описание</Label>
            <Textarea
              id="description"
              rows={2}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Сканируй QR на кассе → получи баллы и выбери удобный канал"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="image_url" className="text-sm">Изображение (URL или SVG)</Label>
              <Textarea
                id="image_url"
                rows={3}
                value={formData.image_url}
                onChange={(e) => handleChange("image_url", e.target.value)}
                placeholder={"https://... или\n<svg xmlns=...>...</svg>"}
                className="text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="logo" className="text-sm">Логотип (эмодзи, текст или SVG)</Label>
              <Textarea
                id="logo"
                rows={3}
                value={formData.logo}
                onChange={(e) => handleChange("logo", e.target.value)}
                placeholder={"💎 PremiumClub\n\nили <svg>...</svg>"}
                className="text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {["utm_source", "utm_medium", "utm_campaign"].map((field) => (
              <div key={field} className="space-y-1.5">
                <Label htmlFor={field} className="text-sm">{field}</Label>
                <Input
                  id={field}
                  value={formData[field]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  placeholder={field === "utm_source" ? "qr_cashier" : field === "utm_medium" ? "qr_physical" : "loyalty_card"}
                  className="text-sm"
                />
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="collect_utm"
              checked={collectUtm}
              onCheckedChange={(checked) => {
                setCollectUtm(checked);
                handleChange("collect_form_utm", checked);
              }}
            />
            <Label htmlFor="collect_utm" className="text-sm text-muted-foreground cursor-pointer">
              Собирать UTM из адресной строки при переходе
            </Label>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="auto_tags" className="text-sm">Автоматические теги (через запятую)</Label>
            <Input
              id="auto_tags"
              value={formData.auto_tags}
              onChange={(e) => handleChange("auto_tags", e.target.value)}
              placeholder="offline, cashier, loyalty, summer2025"
            />
            <p className="text-xs text-muted-foreground">
              Эти теги будут автоматически проставлены на каждый переход
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Статус</Label>
            <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="active">Активна</SelectItem>
                <SelectItem value="draft">Черновик</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Сохранение..." : "Сохранить"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CampaignsTable = ({ pages, loading, onEdit, onDelete, onPreview, onLog, onStats }) => {
  const handleDownloadQR = (page) => {
    const link = document.createElement("a");
    link.href = QR_SRC(page.short_code, 300);
    link.download = `qr_${page.short_code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR-код скачивается");
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="text-center py-16 text-sm text-muted-foreground">
        Нет кампаний. Создайте первую.
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="text-xs">Источник / Кампания</TableHead>
            <TableHead className="text-xs text-center">Переходов</TableHead>
            <TableHead className="text-xs text-center">Кликов</TableHead>
            <TableHead className="text-xs text-center">Регистраций</TableHead>
            <TableHead className="text-xs text-center">CR%</TableHead>
            <TableHead className="text-xs">Создана</TableHead>
            <TableHead className="text-xs">Статус</TableHead>
            <TableHead className="text-xs">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pages.map((page) => (
            <TableRow key={page.id} className="group">
              <TableCell className="py-3">
                <div className="space-y-0.5 max-w-xs">
                  <p className="text-sm">{page.source_name || page.name}</p>
                  {page.source_name && (
                    <p className="text-xs text-muted-foreground">{page.name}</p>
                  )}
                  {page.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{page.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {page.auto_tags?.map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 pt-0.5">
                    <span className="text-[11px] text-muted-foreground">
                      {SHORT_URL(page.short_code)}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${QR_DOMAIN}/qr/${page.short_code}`);
                        toast.success("Ссылка скопирована");
                      }}
                      className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-center text-sm tabular-nums text-blue-600">
                {page.stats?.visits ?? 0}
              </TableCell>
              <TableCell className="text-center text-sm tabular-nums text-orange-500">
                {page.stats?.clicks ?? 0}
              </TableCell>
              <TableCell className="text-center text-sm tabular-nums text-green-600">
                {page.stats?.registrations ?? 0}
              </TableCell>
              <TableCell className="text-center text-sm tabular-nums text-purple-600">
                {page.stats?.conversion ?? 0}%
              </TableCell>
              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                {page.created_at ? new Date(page.created_at).toLocaleDateString("ru-RU") : "—"}
              </TableCell>
              <TableCell>
                <Badge
                  variant={page.status === "active" ? "default" : "secondary"}
                  className="text-xs"
                >
                  {page.status === "active" ? "Активна" : "Черновик"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-0.5 transition-opacity">
                  <TooltipProvider>
                    {[
                      { icon: Eye, label: "Превью", action: () => onPreview(page) },
                      { icon: QrCode, label: "Скачать QR", action: () => handleDownloadQR(page) },
                      { icon: BarChart3, label: "Аналитика", action: () => onStats(page) },
                      { icon: Users, label: "Визиты", action: () => onLog(page) },
                      { icon: Pencil, label: "Редактировать", action: () => onEdit(page) },
                    ].map(({ icon: Icon, label, action }) => (
                      <Tooltip key={label}>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={action}>
                            <Icon className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">{label}</TooltipContent>
                      </Tooltip>
                    ))}
                  </TooltipProvider>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Удалить кампанию?</AlertDialogTitle>
                        <AlertDialogDescription>Это действие нельзя отменить.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(page.id)}>Удалить</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const StatCard = ({ label, value, className }) => (
  <div className="rounded-lg border p-4 space-y-1">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl tabular-nums ${className}`}>{value}</p>
  </div>
);

const AnalyticsTab = ({ pages, selId, onSel, stats, statsLoading }) => {
  const barData = pages.map((p) => ({
    name: (p.source_name || p.name).slice(0, 16),
    Переходы: p.stats?.visits || 0,
    Регистрации: p.stats?.registrations || 0,
    Клики: p.stats?.clicks || 0,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Сравнение кампаний</CardTitle>
          </CardHeader>
          <CardContent>
            {barData.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData} margin={{ left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Переходы" fill="#1677ff" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Регистрации" fill="#52c41a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Клики" fill="#fa8c16" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-10 text-sm text-muted-foreground">Нет данных</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Детальная аналитика</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              value={selId?.toString() || ""}
              onValueChange={(v) => onSel(v ? Number(v) : null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите кампанию" />
              </SelectTrigger>
              <SelectContent position="popper">
                {pages.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.source_name ? `${p.source_name} — ` : ""}
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {statsLoading && (
              <div className="space-y-2">
                {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            )}

            {selId && stats?.summary && !statsLoading && (
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Переходы" value={stats.summary.visits} className="text-blue-600" />
                <StatCard label="Регистрации" value={stats.summary.registrations} className="text-green-600" />
                <StatCard label="Клики" value={stats.summary.clicks} className="text-orange-500" />
                <StatCard label="CR%" value={`${stats.summary.conversion}%`} className="text-purple-600" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selId && stats && !statsLoading && <DetailCharts stats={stats} />}
    </div>
  );
};

export default function LoyaltyQRPage({ token, websocket, initialData }) {
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("campaigns");

  const [formOpen, setFormOpen] = useState(false);
  const [formInit, setFormInit] = useState(null);

  const [previewPage, setPreviewPage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [visitsPage, setVisitsPage] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitsOpen, setVisitsOpen] = useState(false);
  const [visitsLoading, setVisitsLoading] = useState(false);

  const [selPageId, setSelPageId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    apiRequests.setToken(token);
  }, [token]);

  const reload = useCallback(async () => {
    setPagesLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getPages();
      const data = response.data;
      setPages(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Ошибка загрузки кампаний");
    } finally {
      setPagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) reload();
  }, [token, reload]);

  const handleSave = useCallback(
    async (vals) => {
      if (formInit?.id) {
        await apiRequests.loyaltyQr.updatePage(formInit.id, vals);
        toast.success("Обновлено");
      } else {
        await apiRequests.loyaltyQr.createPage(vals);
        toast.success("Кампания создана");
      }
      setFormOpen(false);
      setFormInit(null);
      reload();
    },
    [formInit, reload]
  );

  const handleDelete = useCallback(
    async (id) => {
      await apiRequests.loyaltyQr.deletePage(id);
      toast.success("Удалено");
      reload();
    },
    [reload]
  );

  const handleLog = useCallback(async (page) => {
    setVisitsPage(page);
    setVisitsOpen(true);
    setVisitsLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getVisits(page.id);
      const data = response.data;
      setVisits(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Ошибка загрузки визитов");
    } finally {
      setVisitsLoading(false);
    }
  }, []);

  const handleSelPage = useCallback(async (id) => {
    setSelPageId(id);
    if (!id) {
      setStats(null);
      return;
    }
    setStatsLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getStats(id, 7);
      const data = response.data;
      setStats(data);
    } catch {
      toast.error("Ошибка загрузки статистики");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const handleStatsBtn = useCallback(
    (page) => {
      setActiveTab("analytics");
      handleSelPage(page.id);
    },
    [handleSelPage]
  );

  return (
    <div className="pt-4 space-y-5">
      <div className="flex items-center justify-between">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between gap-1 mb-5 h-6">
            <TabsList>
              <TabsTrigger value="campaigns" className="gap-1.5 text-sm h-12 w-40">
                <QrCode className="h-6 w-6" />
                Кампании
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-1.5 text-sm h-12 w-40">
                <BarChart3 className="h-6 w-6" />
                Аналитика
              </TabsTrigger>
            </TabsList>

            <div className="flex gap-2">
              <Button variant="outline" size="lg" onClick={reload} disabled={pagesLoading}>
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Обновить
              </Button>
              <Button
                size="lg"
                onClick={() => {
                  setFormInit(null);
                  setFormOpen(true);
                }}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Новая кампания
              </Button>
            </div>
          </div>

          <TabsContent value="campaigns">
            <CampaignsTable
              pages={pages}
              loading={pagesLoading}
              onEdit={(r) => {
                setFormInit(r);
                setFormOpen(true);
              }}
              onDelete={handleDelete}
              onPreview={(r) => {
                setPreviewPage(r);
                setPreviewOpen(true);
              }}
              onLog={handleLog}
              onStats={handleStatsBtn}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab
              pages={pages}
              selId={selPageId}
              onSel={handleSelPage}
              stats={stats}
              statsLoading={statsLoading}
            />
          </TabsContent>
        </Tabs>
      </div>

      <CampaignFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        onSave={handleSave}
        initial={formInit}
      />

      <PreviewModal open={previewOpen} onOpenChange={setPreviewOpen} page={previewPage} />

      <VisitsModal
        open={visitsOpen}
        onOpenChange={setVisitsOpen}
        page={visitsPage}
        visits={visits}
        loading={visitsLoading}
      />
    </div>
  );
}